import { apiStatic } from 'AurionCR/components';
import { pathStepForecast } from 'components/rating-process-preview/step-forecast';
import { pathStepInformationRequest } from 'components/rating-process-preview/step-information-request';
import { pathStepReport } from 'components/rating-process-preview/step-report';
import { parseGetUrlToPost, queryEncoderObject } from 'components/helpers';
import { API_RATING_PROCESS_STEP_COMMITTEE } from 'services/rating-process-step-committee';
import { API_RATING_PROCESS_STEP_COMMITTEE_RATING_MATRIX_FRAMEWORK } from 'services/rating-process-step-committee-rating-matrix-framework';
import { API_RATING_PROCESS_STEP_COMMITTEE_MATERIAL } from 'services/rating-process-step-committee-material';
import { API_RATING_PROCESS_STEP_COMMITTEE_RESOURCE } from 'services/rating-process-step-committee-resource';
import { API_RATING_PROCESS_STEP_COMMITTEE_SERIES_RATING } from 'services/rating-process-step-committee-series-rating';
import { API_RATING_PROCESS_STEP_COMMITTEE_SIGNIFICANT_CHANGE } from 'services/rating-process-step-committee-significant-change';
import { API_RATING_PROCESS_STEP_COMMITTEE_USER } from 'services/rating-process-step-committee-user';
import { API_RATING_PROCESS_STEP_COMMITTEE_USER_ENTITY_VOTING } from 'services/rating-process-step-committee-user-rates-entity-voting';
import { API_RATING_PROCESS_STEP_COMMITTEE_USER_SERIES_VOTING } from 'services/rating-process-step-committee-user-series-voting';
import { API_RATING_PROCESS_STEP_PUBLICATION } from 'services/rating-process-step-publication';
import { API_RATING_PROCESS_STEP_PUBLICATION_CHECKUP } from 'services/rating-process-step-publication-checkup';
import { API_USER_DETAIL } from 'services/user-detail';
import { sortBy } from 'lodash';
import { getIsConflictOfInterests } from 'components/helpers';
import { RatingProcessStep, TranslateFunction, RatingProcessData } from './types';
import { dateHelper, linkHelper, getHtml, psdStyle } from './helpers';
import { generatePageTitle } from './page-title';

/**
 * Generates HTML for step type 1 (Information Request)
 */
const generateStepType1 = async (
  t: TranslateFunction,
  step: RatingProcessStep,
  ratingProcessID: number,
): Promise<string> => {
  const {
    data: {
      value: { [0]: stepData },
    },
  } = await apiStatic.post(...parseGetUrlToPost(pathStepInformationRequest(ratingProcessID)));

  stepData.discussionDate = dateHelper(stepData?.discussionDate);
  stepData.requestMaterialsDate = dateHelper(stepData?.requestMaterialsDate);
  stepData.responseMaterialsDate = dateHelper(stepData?.responseMaterialsDate);

  return `<div style='page-break-before: always;'>
    <h1 class='pageTitle'>${step?.ratingProcessStepTypeTitle || ''}</h1>
    <table class='tablePreview'>
      <tbody>
      <tr>
        <th>${t('discussion-date')}</th>
        <td>${stepData.discussionDate || ''}</td>    
      </tr>
       <tr>
        <th>${t('request-materials-date')}</th>
        <td>${stepData.requestMaterialsDate || ''}</td>    
      </tr>
       <tr>
        <th>${t('response-materials-date')}</th>
        <td>${stepData.responseMaterialsDate || ''}</td>    
      </tr>
       <tr>
        <th>${t('methodology')}</th>
        <td>${stepData?.primaryMethodologyTitle || ''}</td>    
      </tr>
       <tr>
        <th>${t('sub-methodology')}</th>
        <td>${stepData?.secondaryMethodologyTitle || ''}</td>    
      </tr>
       <tr>
        <th>${t('head-of-field')}</th>
        <td>${stepData?.headOfFieldName || ''}</td>    
      </tr>
       <tr>
        <th>${t('team-leader')}</th>
        <td>${stepData?.teamLeaderName || ''}</td>    
      </tr>
       <tr>
        <th>${t('additional-analyst')}</th>
        <td>${stepData?.analystName || ''}</td>    
      </tr>
      <tr>
        <th>${t('more-members')}</th>
        <td>${stepData?.members || ''}</td>    
      </tr>
      </tbody>
    </table>
  </div>`;
};

/**
 * Generates HTML for step type 2 (Forecast)
 */
const generateStepType2 = async (t: TranslateFunction, step: RatingProcessStep): Promise<string> => {
  const {
    data: { value },
  } = await apiStatic.post(...parseGetUrlToPost(pathStepForecast(step.id)));

  for (let i = 0; i < value.length; i++) {
    value[i].reviewDate = dateHelper(value[i].reviewDate);
    value[i].midroogRatingTitle = value[i]?.midroogRatingTitle
      ? value[i]?.midroogRatingTitle
      : `${value[i]?.minRangeMidroogRatingTitle || '...'} - ${
          value[i]?.maxRangeMidroogRatingTitle || '...'
        }`;
    value[i].review = await getHtml(value[i].review);
  }

  return `<div style='page-break-before: always;'>
    <h1 class='pageTitle'>${step.ratingProcessStepTypeTitle || ''}</h1>
    <ol>
    ${value
      .map(
        (item: any) => `<li>
        <table class='tablePreview'>
          <tbody>
            <tr>
              <th>${t('review-date')}</th>
              <td>${item.reviewDate || ''}</td>    
            </tr>
             <tr>
              <th>${t('members')}</th>
              <td>${item?.members || ''}</td>    
            </tr>
             <tr>
              <th>${t('forecast-rating')}</th>
              <td>${item.midroogRatingTitle || ''}</td>    
            </tr>
             <tr>
              <th>${t('review-remarks')}</th>
              <td class='html'>${item?.review || ''}</td>    
            </tr>
          </tbody>
        </table>
      </li>`,
      )
      .join('')}
  </ol>
  </div>`;
};

/**
 * Helper functions for step type 3
 */
const getMaterials = async (committeeID: number) => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_COMMITTEE_MATERIAL.GET_ALL_DYNAMIC,
    queryEncoderObject({
      filter: `ratingProcessStepCommitteeID==${committeeID}`,
      select: ['title', 'remarks', 'isInclude'].join(),
    }),
  );
  for (let i = 0; i < value.length; i++) {
    value[i].remarks = await getHtml(value[i].remarks);
  }
  return value;
};

const getResources = async (committeeID: number) => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_COMMITTEE_RESOURCE.GET_ALL_DYNAMIC,
    queryEncoderObject({
      filter: `ratingProcessStepCommitteeID==${committeeID}`,
      select: ['title', 'remarks', 'isInclude'].join(),
    }),
  );
  for (let i = 0; i < value.length; i++) {
    value[i].remarks = await getHtml(value[i].remarks);
  }
  return value;
};

const getRatingMatrixFrameworks = async (committeeID: number) => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_COMMITTEE_RATING_MATRIX_FRAMEWORK.GET_ALL_DYNAMIC,
    queryEncoderObject({
      filter: `ratingProcessStepCommitteeID==${committeeID}`,
      select: [
        'ratingMatrixFramework.title as frameworkTitle',
        'ratingMatrixFrameworkTemplate.Version as templateVersion',
        'ratingMatrixFrameworkTemplateID as templateId',
      ].join(','),
    }),
  );
  return value;
};

const getSignificantChange = async (committeeID: number) => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_COMMITTEE_SIGNIFICANT_CHANGE.GET_ALL_DYNAMIC,
    queryEncoderObject({
      filter: `ratingProcessStepCommitteeID==${committeeID}`,
      select: [
        'title',
        'committeeSignificantChangeType.title as committeeSignificantChangeTypeTitle',
        'remarks',
        'isActive',
      ].join(),
    }),
  );
  for (let i = 0; i < value.length; i++) {
    value[i].remarks = await getHtml(value[i].remarks);
  }
  return value;
};

const getUsers = async (committeeID: number) => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_COMMITTEE_USER.GET_ALL_DYNAMIC,
    queryEncoderObject({
      filter: `ratingProcessStepCommitteeID==${committeeID}`,
      select: [
        'userID',
        'user.fullName as userFullName',
        'committeePosition.title as committeePositionTitle',
        'votingOrder',
        'isAllowedToVote',
        'isConflictOfInterests',
      ].join(),
      orderBy: 'votingOrder',
    }),
  );
  return value.map((item: any) => ({
    ...item,
    isConflictOfInterests: getIsConflictOfInterests(item.isConflictOfInterests),
  }));
};

const getUserRatEntVot = async (committeeID: number, rankMap: Record<string, number>) => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_COMMITTEE_USER_ENTITY_VOTING.GET_ALL_DYNAMIC,
    queryEncoderObject({
      filter: `ratingProcessStepCommitteeID==${committeeID}`,
      select: [
        'userID',
        'user.fullName as userFullName',
        'committeePosition.title as committeePositionTitle',
        'longTermMidroogRating.title as longTermMidroogRatingTitle',
        'midroogOutlook.title as midroogOutlookTitle',
      ].join(),
    }),
  );
  return sortBy(
    value.map((item: any) => {
      item.rank = rankMap[String(item.userID)] !== undefined ? rankMap[String(item.userID)] : 10000;
      return item;
    }),
    ['rank'],
  );
};

const getSeriesRating = async (committeeID: number) => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_COMMITTEE_SERIES_RATING.GET_ALL_DYNAMIC,
    queryEncoderObject({
      filter: `ratingProcessStepCommitteeID==${committeeID}`,
      select: [
        'series.title as seriesTitle',
        'midroogRating.title as midroogRatingTitle',
        'midroogOutlook.title as midroogOutlookTitle',
        'midroogRatingSymbol.title as midroogRatingSymbol',
        'provisional',
      ].join(),
    }),
  );
  return value;
};

const getUserSeriesVoting = async (committeeID: number, rankMap: Record<string, number>) => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_COMMITTEE_USER_SERIES_VOTING.GET_ALL_DYNAMIC,
    queryEncoderObject({
      filter: `ratingProcessStepCommitteeID==${committeeID}`,
      select: [
        'seriesID',
        'userID',
        'user.fullName as userFullName',
        'series.title as seriesTitle',
        'midroogRating.title as midroogRatingTitle',
        'midroogOutlook.title as midroogOutlookTitle',
        'isActive',
      ].join(),
    }),
  );
  return sortBy(
    value.map((item: any) => {
      item.rank = rankMap[String(item.userID)] !== undefined ? rankMap[String(item.userID)] : 10000;
      return item;
    }),
    ['rank', 'seriesID'],
  );
};

/**
 * Generates HTML for step type 3 (Committee)
 */
const generateStepType3 = async (t: TranslateFunction, step: RatingProcessStep): Promise<string> => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_COMMITTEE.GET_ALL_DYNAMIC,
    queryEncoderObject({
      select: [
        'id',
        'committeeDate',
        'lastVotingDate',
        'committeeManner.title as committeeMannerTitle',
        'examinedFinancialStatementDate',
        'isAdditionalPublications',
        'isAdditionalMaterialsLate',
        'isMatrixModelSaved',
        'isHeadOfFieldFormulaAndParamsApproved',
        'remarks',
        'ratingDecisionRemarks',
        'restrictions',
        'discussionIssueses',
        'isMaterialsUpdated',
        'isMaterialsVolumeEnough',
        'isMaterialsReliable',
        'materialsReservations',
        'longTermMidroogRating.title as longTermMidroogRatingTitle',
        'shortTermMidroogRating.title as shortTermMidroogRatingTitle',
        'baselineCreditAssesmentRating.title as baselineCreditAssesmentRatingTitle',
        'midroogOutlook.title as midroogOutlookTitle',
        'midroogRatingSymbol.title as midroogRatingSymbolTitle',
        'longTermCapitalApproved',
        'longTermCapital',
        'shortTermCapitalApproved',
        'shortTermCapital',
        'isChangeRatingMethod',
        'changeRatingMethodRemarks',
        'isRatingChangeImpact',
        'ratingChangeImpactRemarks',
      ].join(),
      filter: `ratingProcessStepID==${step.id}`,
    }),
  );

  for (let i = 0; i < value.length; i++) {
    const committee = value[i];
    
    // Load all related data in parallel
    const [materials, resources, significantChanges, users, ratingMatrixFrameworks] = await Promise.all([
      getMaterials(committee.id),
      getResources(committee.id),
      getSignificantChange(committee.id),
      getUsers(committee.id),
      getRatingMatrixFrameworks(committee.id),
    ]);

    committee.materials = materials;
    committee.resources = resources;
    committee.significantChanges = significantChanges;
    committee.users = users;
    committee.ratingMatrixFrameworkTitles = (ratingMatrixFrameworks || [])
      .map((item: { frameworkTitle?: string; templateVersion?: string | null; templateId?: number | null }) => {
        const title = item.frameworkTitle || '';
        if (item.templateId == null) {
          return `${title} (${t('committee-matrix-version-missing')})`;
        }
        if (item.templateVersion) {
          return `${title} (${item.templateVersion})`;
        }
        return title;
      })
      .filter(Boolean)
      .join(', ');

    // Create rank map
    const rankMap: Record<string, number> = users.reduce(
      (acc: any, item: any, index: number) => {
        if (item.userID) {
          acc[String(item.userID)] = index;
        }
        return acc;
      },
      {},
    );

    // Load voting data
    const [userRatEntVot, userSeriesVoting, seriesRating] = await Promise.all([
      getUserRatEntVot(committee.id, rankMap),
      getUserSeriesVoting(committee.id, rankMap),
      getSeriesRating(committee.id),
    ]);

    committee.userRatEntVot = userRatEntVot;
    committee.userSeriesVoting = userSeriesVoting;
    committee.seriesRating = seriesRating;

    // Format dates and HTML
    committee.committeeDate = dateHelper(committee.committeeDate);
    committee.lastVotingDate = dateHelper(committee.lastVotingDate);
    committee.examinedFinancialStatementDate = dateHelper(committee.examinedFinancialStatementDate);
    committee.remarks = await getHtml(committee.remarks);
    committee.ratingDecisionRemarks = await getHtml(committee.ratingDecisionRemarks);
    committee.restrictions = await getHtml(committee.restrictions);
    committee.discussionIssueses = await getHtml(committee.discussionIssueses);
    committee.materialsReservations = await getHtml(committee.materialsReservations);
    committee.changeRatingMethodRemarks = await getHtml(committee.changeRatingMethodRemarks);
    committee.ratingChangeImpactRemarks = await getHtml(committee.ratingChangeImpactRemarks);
  }

  // Generate HTML (using the same template from step-type-3.ts)
  return `<div style='page-break-before: always;'>
    <h1 class='pageTitle'>${step.ratingProcessStepTypeTitle || ''}</h1>
    <ol>
   ${value
     .map(
       (item: any) => `<li>
        <h3>${t('preliminary')}</h3>
        <table class='tablePreview'>
          <tbody>
            <tr>
              <th>${t('committee-date')}</th>
              <td>${item.committeeDate || ''}</td>    
            </tr>
            <tr>
              <th>${t('last-voting-date')}</th>
              <td>${item.lastVotingDate || ''}</td>    
            </tr>
            <tr>
              <th>${t('committee-manner')}</th>
              <td>${item.committeeMannerTitle || ''}</td>    
            </tr>
            <tr>
              <th>${t('examined-financial-statement-date')}</th>
              <td>${item.examinedFinancialStatementDate || ''}</td>    
            </tr>
            <tr>
              <th>${t('additional-publications')}</th>
              <td>${item.isAdditionalPublications ? '&#10003;' : '&#9744;'}</td>    
            </tr>
            <tr>
              <th>${t('additional-materials-late')}</th>
              <td>${item.isAdditionalMaterialsLate ? '&#10003;' : '&#9744;'}</td>    
            </tr>
            <tr>
              <th>${t('rating-matrix-frameworks')}</th>
              <td>${item.ratingMatrixFrameworkTitles || ''}</td>    
            </tr>
            <tr>
              <th>${t('is-matrix-model-saved')}</th>
              <td>${item.isMatrixModelSaved ? '&#10003;' : '&#9744;'}</td>    
            </tr>
            <tr>
              <th>${t('is-head-fo-field-formula-and-params-approved')}</th>
              <td>${item.isHeadOfFieldFormulaAndParamsApproved ? '&#10003;' : '&#9744;'}</td>    
            </tr>
            <tr>
              <th>${t('remarks')}</th>
              <td class='html'>${item.remarks || '-'}</td>    
            </tr>
          </tbody>
        </table>
        <h4>${t('members')}</h4>
        <table class='tableStandard tableTdCenter full-width'>
          <thead>
            <tr>
              <th>${t('name')}</th>
              <th>${t('committee-position')}</th>
              <th>${t('voting-order')}</th>
              <th>${t('is-allowed-to-vote')}</th>
              <th>${t('is-conflict-of-interests')}</th>
            </tr>
          </thead>
          <tbody>
          ${(item.users || [])
            .map(
              (user: any) => `<tr>
                <td>${user.userFullName || ''}</td>
                <td>${user.committeePositionTitle || ''}</td>
                <td>${user.votingOrder || ''}</td>    
                <td>${user.isAllowedToVote ? '&#10003;' : '&#9744;'}</td>    
                <td>${t(user.isConflictOfInterests)}</td>    
              </tr>`,
            )
            .join('')}
          </tbody>
        </table>
        <h3>${t('committee-materials')}</h3>
        <table class='tablePreview full-width'>
          <tbody>
          ${(item.materials || [])
            .map(
              (material: any, i: number) => `<tr>
                <th>${i + 1}.</th>
                <th>${material.isInclude ? '&#10003;' : '&#9744;'}</th>
                <th>${material.title || ''}</th>    
                <td class='html'>${material.remarks || '-'}</td>    
              </tr>`,
            )
            .join('')}
          </tbody>
        </table>
        <h3>${t('committee-resources')}</h3>
        <table class='tablePreview full-width'>
          <tbody>
          ${(item.resources || [])
            .map(
              (resource: any, i: number) => `<tr>
                <th>${i + 1}.</th>
                <th>${resource.isInclude ? '&#10003;' : '&#9744;'}</th>
                <th>${resource.title || ''}</th>    
                <td class='html'>${resource.remarks || '-'}</td>    
              </tr>`,
            )
            .join('')}
          </tbody>
        </table>
        <h3>${t('rating-decisions')}</h3>
        <p class='html'>${item.ratingDecisionRemarks || '-'}</p>
        <h3>${t('significant-changes')}</h3>
        <table class='tablePreview full-width'>
          <tbody>
          ${(item.significantChanges || [])
            .map(
              (significantChange: any, i: number) => `<tr>
                <th>${i + 1}.</th>
                <th>${significantChange.isActive ? '&#10003;' : '&#9744;'}</th>
                <th>${significantChange.title || ''}</th>    
                <th>${significantChange.committeeSignificantChangeTypeTitle || ''}</th>    
                <td class='html'>${significantChange.remarks || '-'}</td>    
              </tr>`,
            )
            .join('')}
          </tbody>
        </table>
        <h3>${t('restrictions')}</h3>
        <div class='html'>${item.restrictions || '-'}</div>
        <h3>${t('other-issues')}</h3>
        <div class='html'>${item.discussionIssueses || '-'}</div>
        <h3>${t('committee-rated-entity-votings')}</h3>
        <h4>${t('materials-before-committee')}</h4>
        <table class='tablePreview'>
          <tbody>
            <tr>
              <th>${t('materials-updated')}</th>
              <td>${item.isMaterialsUpdated ? '&#10003;' : '&#9744;'}</td>    
            </tr>
            <tr>
              <th>${t('materials-volume-enough')}</th>
              <td>${item.isMaterialsVolumeEnough ? '&#10003;' : '&#9744;'}</td>    
            </tr>
            <tr>
              <th>${t('materials-reliable')}</th>
              <td>${item.isMaterialsReliable ? '&#10003;' : '&#9744;'}</td>    
            </tr>
            <tr>
              <th>${t('materials-reservations')}</th>
              <td class='html'>${item.materialsReservations || ''}</td>    
            </tr>
          </tbody>
        </table>
        <h4>${t('committee-rated-entity-votings')}</h4>
        <table class='tableStandard tableTdCenter full-width'>
          <thead>
            <tr>
              <th>${t('name')}</th>
              <th>${t('committee-position')}</th>
              <th>${t('long-term-rating')}</th>
              <th>${t('outlook')}</th>
            </tr>
          </thead>
          <tbody>
          ${(item.userRatEntVot || [])
            .map(
              (user: any) => `<tr>
                <td>${user.userFullName || ''}</td>
                <td>${user.committeePositionTitle || ''}</td>
                <td>${user.longTermMidroogRatingTitle || ''}</td>    
                <td>${t(user.midroogOutlookTitle || '')}</td>    
              </tr>`,
            )
            .join('')}
          </tbody>
        </table>
        <h4>${t('series-voting')}</h4>
        <table class='tableStandard tableTdCenter full-width'>
          <thead>
            <tr>
              <th>${t('series')}</th>
              <th>${t('midroog-rating')}</th>
              <th>${t('midroog-outlook')}</th>
              <th>${t('midroog-rating-symbol')}</th>
              <th>${t('provisional')}</th>
            </tr>
          </thead>
          <tbody>
          ${(item.seriesRating || [])
            .map(
              (rating: any) => `<tr>
                <td>${rating.seriesTitle || ''}</td>
                <td>${rating.midroogRatingTitle || ''}</td>
                <td>${rating.midroogOutlookTitle || ''}</td>    
                <td>${rating.midroogRatingSymbol || '-'}</td>    
                <td>${rating.provisional ? '&#10003;' : '&#9744;'}</td>    
              </tr>`,
            )
            .join('')}
          </tbody>
        </table>
        <h4>${t('analysts-voting-for-series')}</h4>
        <table class='tableStandard tableTdCenter full-width'>
          <thead>
            <tr>
              <th>${t('name')}</th>
              <th>${t('series')}</th>
              <th>${t('midroog-rating')}</th>
              <th>${t('midroog-outlook')}</th>
            </tr>
          </thead>
          <tbody>
          ${(item.userSeriesVoting || [])
            .map(
              (rating: any) => `<tr>
                <td>${rating.userFullName || ''}</td>
                <td>${rating.seriesTitle || ''}</td>
                <td>${rating.midroogRatingTitle || ''}</td>
                <td>${rating.midroogOutlookTitle || ''}</td>    
              </tr>`,
            )
            .join('')}
          </tbody>
        </table>
        <h4>${t('materials-before-committee')}</h4>
        <table class='tablePreview'>
          <tbody>
            <tr>
              <th>${t('long-term-midroog-rating')}</th>
              <td>${item.longTermMidroogRatingTitle || ''}</td>    
            </tr>
            <tr>
              <th>${t('short-term-midroog-rating')}</th>
              <td>${item.shortTermMidroogRatingTitle || ''}</td>    
            </tr>
            <tr>
              <th>${t('baseline-credit-assesment-rating')}</th>
              <td>${item.baselineCreditAssesmentRatingTitle || ''}</td>    
            </tr>
            <tr>
              <th>${t('midroog-outlook')}</th>
              <td>${item.midroogOutlookTitle || ''}</td>    
            </tr>
            <tr>
              <th>${t('midroog-rating-symbol')}</th>
              <td>${item.midroogRatingSymbolTitle || ''}</td>    
            </tr>
            <tr>
              <th>${t('long-term-capital-approved')}</th>
              <td>${item.longTermCapitalApproved || ''}</td>    
            </tr>
            <tr>
              <th>${t('long-term-capital')}</th>
              <td>${item.longTermCapital || ''}</td>    
            </tr>
            <tr>
              <th>${t('short-term-capital-approved')}</th>
              <td>${item.shortTermCapitalApproved || ''}</td>    
            </tr>
            <tr>
              <th>${t('short-term-capital')}</th>
              <td>${item.shortTermCapital || ''}</td>    
            </tr>
          </tbody>
        </table>
        <h3>${t('compliance-officer-issues')}</h3>
        <div class='html'>${item.isChangeRatingMethod ? '&#10003;' : '&#9744;'} ${t(
          'change-rating-method',
        )}</div>
        ${item.isChangeRatingMethod ? `<div class='html'>${item.changeRatingMethodRemarks}</div>` : '-'}
        <div class='html'>${item.isRatingChangeImpact ? '&#10003;' : '&#9744;'} ${t(
          'rating-change-impact',
        )}</div>
        ${item.isRatingChangeImpact ? `<div class='html'>${item.ratingChangeImpactRemarks}</div>` : '-'}
      </li>`,
     )
     .join('')}
  </ol>
  </div>`;
};

/**
 * Generates HTML for step type 4 (Report)
 */
const generateStepType4 = async (t: TranslateFunction, step: RatingProcessStep): Promise<string> => {
  const {
    data: { value },
  } = await apiStatic.post(...parseGetUrlToPost(pathStepReport(step.id)));

  for (let i = 0; i < value.length; i++) {
    value[i].draftReport = linkHelper(value[i].draftReport, t('download'));
    value[i].reportDate = dateHelper(value[i].reportDate);
    value[i].draftRequestDate = dateHelper(value[i].draftRequestDate);
    value[i].draftResposeDate = dateHelper(value[i].draftResposeDate);
    value[i].teamLeaderSignatureDate = dateHelper(value[i].teamLeaderSignatureDate);
    value[i].remarks = await getHtml(value[i].remarks);
  }

  return `<div style='page-break-before: always;'>
    <h1 class='pageTitle'>${step.ratingProcessStepTypeTitle || ''}</h1>
    <ol>
      ${value
        .map(
          (item: any) => `<li>
          <table class='tablePreview'>
            <tbody>
              <tr>
                <th>${t('draft-report')}</th>
                <td>${item.draftReport || ''}</td>    
              </tr>
              <tr>
                <th>${t('report-date')}</th>
                <td>${item.reportDate || ''}</td>    
              </tr>
              <tr>
                <th>${t('draft-request-date')}</th>
                <td>${item.draftRequestDate || ''}</td>    
              </tr>
              <tr>
                <th>${t('draft-response-date')}</th>
                <td>${item.draftResposeDate || ''}</td>    
              </tr>
              <tr>
                <th>${t('team-leader')}</th>
                <td>${item.teamLeader || ''}</td>    
              </tr>
              <tr>
                <th>${t('team-leader-signature-date')}</th>
                <td>${item.teamLeaderSignatureDate || ''}</td>    
              </tr>
              <tr>
                <th>${t('issuer-contact-name')}</th>
                <td>${item.issuerContactName || ''}</td>    
              </tr>
              <tr>
                <th>${t('issuer-contact-senior-officer')}</th>
                <td>${item.isIssuerContactSeniorOfficer ? 'yes' : 'no'}</td>    
              </tr>
              <tr>
                <th>${t('the-client-decision')}</th>
                <td>${item.clientDecision || ''}</td>    
              </tr>
              <tr>
                <th>${t('remarks')}</th>
                <td class='html'>${item.remarks || ''}</td>    
              </tr>
            </tbody>
          </table>
        </li>`,
        )
        .join('')}
    </ol>
  </div>`;
};

/**
 * Generates HTML for step type 5 (Publication)
 */
const generateStepType5 = async (t: TranslateFunction, step: RatingProcessStep): Promise<string> => {
  const {
    data: {
      value: { [0]: data },
    },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_PUBLICATION.GET_ALL_DYNAMIC,
    queryEncoderObject({
      select: [
        'id',
        'teamLeaderUserID',
        'headOfFieldUserID',
        'analystUserID',
        'teamLeaderSignatureDate',
        'headOfFieldSignatureDate',
        'analystSignatureDate',
        'reportPublicationOption.title as reportPublicationOptionTitle',
        'magnaPublicationDate',
        'publicationApprovalType.title as publicationApprovalTypeTitle',
        'report.filePath as reportFilePath',
        'confirmationFilePath',
        'remarks',
      ].join(),
      take: 1,
      filter: `ratingProcessStepID==${step.id}`,
    }),
  );

  if (!data) {
    return `<div style='page-break-before: always;'>
      <h1 class='pageTitle'>${step.ratingProcessStepTypeTitle || ''}</h1>
      <p>No data available</p>
    </div>`;
  }

  const {
    data: { value: checkups },
  } = await apiStatic.post(
    API_RATING_PROCESS_STEP_PUBLICATION_CHECKUP.GET_ALL_DYNAMIC,
    queryEncoderObject({
      select: [
        'title',
        'reportCheckupType.title as reportCheckupTypeTitle',
        'headOfFieldApproval',
        'analystApproval',
        'remarks',
      ].join(),
      orderBy: 'rank asc',
      filter: `ratingProcessStepPublicationID==${data?.id}`,
    }),
  );

  const {
    data: { value: usersList },
  } = await apiStatic.post(
    API_USER_DETAIL.GET_ALL_DYNAMIC,
    queryEncoderObject({ select: 'userID,fullName' }),
  );

  const users = usersList.reduce((acc: any, item: any) => {
    acc[item.userID] = item.fullName;
    return acc;
  }, {});

  data.teamLeaderUserFullName = users[data.teamLeaderUserID] || '';
  data.headOfFieldUserFullName = users[data.headOfFieldUserID] || '';
  data.analystUserFullName = users[data.analystUserID] || '';
  data.teamLeaderSignatureDate = dateHelper(data.teamLeaderSignatureDate, 'yyyy.MM.dd hh:mm');
  data.headOfFieldSignatureDate = dateHelper(data.headOfFieldSignatureDate, 'yyyy.MM.dd hh:mm');
  data.analystSignatureDate = dateHelper(data.analystSignatureDate, 'yyyy.MM.dd hh:mm');
  data.magnaPublicationDate = dateHelper(data.magnaPublicationDate, 'yyyy.MM.dd hh:mm');
  data.remarks = await getHtml(data.remarks);
  data.reportFilePath = linkHelper(data.reportFilePath, t('download'));
  data.confirmationFilePath = linkHelper(data.confirmationFilePath, t('download'));

  for (let i = 0; i < checkups.length; i++) {
    checkups[i].remarks = await getHtml(checkups[i].remarks);
  }

  return `<div style='page-break-before: always;'>
    <h1 class='pageTitle'>${step.ratingProcessStepTypeTitle || ''}</h1>
    <table class='tableStandard tableTdCenter full-width'>
      <thead>
        <tr>
          <th>${t('analyst')}</th>
          <th>${t('head-of-field')}</th>
          <th>${t('additional-analyst')}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>${data.teamLeaderUserFullName}</td>
          <td>${data.headOfFieldUserFullName}</td>
          <td>${data.analystUserFullName}</td>    
        </tr>
        <tr>
          <td>${data.teamLeaderSignatureDate}</td>
          <td>${data.headOfFieldSignatureDate}</td>
          <td>${data.analystSignatureDate}</td>    
        </tr>
      </tbody>
    </table>
    <table class='tablePreview'>
      <tbody>
        <tr>
          <th>${t('publication')}</th>
          <td>${data.reportPublicationOptionTitle || ''}</td>
        </tr>
        <tr>
          <th>${t('final-report-publication-date')}</th>
          <td>${data.magnaPublicationDate || ''}</td>
        </tr>
        <tr>
          <th>${t('publication-approval-type')}</th>
          <td>${data.publicationApprovalTypeTitle || ''}</td>
        </tr>
        <tr>
          <th>${t('confirmation-file')}</th>
          <td>${data.confirmationFilePath || ''}</td>
        </tr>
        <tr>
          <th>${t('report')}</th>
          <td>${data.reportFilePath || ''}</td>
        </tr>
        <tr>
          <th>${t('remarks')}</th>
          <td class='html'>${data.remarks || ''}</td>
        </tr>
      </tbody>
    </table>
    <h3>${t('checkups')}</h3>
    <ol>
    ${checkups
      .map(
        (item: any) => `<li>
          <table class='tablePreview'>
            <tbody>
              <tr>
                <th>${t('title')}</th>
                <td>${item.title || ''}</td>    
              </tr>
              <tr>
                <th>${t('report-checkup-type')}</th>
                <td>${item.reportCheckupTypeTitle || ''}</td>    
              </tr>
              <tr>
                <th>${t('head-of-field-approval')}</th>
                <td>${item.headOfFieldApproval ? '&#10003;' : '&#9744;'}</td>    
              </tr>
              <tr>
                <th>${t('analyst-approval')}</th>
                <td>${item.analystApproval ? '&#10003;' : '&#9744;'}</td>    
              </tr>
              <tr>
                <th>${t('remarks')}</th>
                <td class='html'>${item.remarks || ''}</td>    
              </tr>
            </tbody>
          </table>
        </li>`,
      )
      .join('')}
    </ol>
  </div>`;
};

/**
 * Generates complete PDF HTML from rating process data
 */
export const generatePdfHtml = async (
  mainData: RatingProcessData,
  t: TranslateFunction,
): Promise<string> => {
  const pageTitle = generatePageTitle(t, mainData);
  const pages: string[] = [pageTitle];

  const steps = mainData.ratingProcessSteps.filter(
    (step) => step.ratingProcessStepTypeID !== 1000,
  );

  for (const step of steps) {
    let stepHtml = '';
    try {
      switch (step.ratingProcessStepTypeID) {
        case 1:
          stepHtml = await generateStepType1(t, step, mainData.id);
          break;
        case 2:
          stepHtml = await generateStepType2(t, step);
          break;
        case 3:
          stepHtml = await generateStepType3(t, step);
          break;
        case 4:
          stepHtml = await generateStepType4(t, step);
          break;
        case 5:
          stepHtml = await generateStepType5(t, step);
          break;
        default:
          stepHtml = '';
      }
      if (stepHtml) {
        pages.push(stepHtml);
      }
    } catch (error) {
      console.error(`Error generating step ${step.ratingProcessStepTypeID}:`, error);
      // Continue with other steps even if one fails
    }
  }

  return `<html dir='rtl'><head>${psdStyle}</head><body>${pages.join('')}</body></html>`;
};

